import { type AxiosResponse } from 'axios'
import { api } from './api'
import { normalizePaginationMeta } from '@/types/paginationProps'
import {
  ArtistOptionProps,
  EventDetailProps,
  EventDiscoveryStatus,
  EventListFiltersProps,
  EventScheduleEntryProps,
  EventScheduleItemProps,
  EventListResponseProps,
  EventMutationPayloadProps,
  EventPerformanceProps,
  EventPoleProps,
  EventSummaryProps,
  PerformanceMutationPayloadProps,
  PoleMutationPayloadProps,
  ScheduleItemMutationPayloadProps,
  ScheduleItemStatus,
  ScheduleItemType,
} from '@/types/eventsProps'

type RecordLike = Record<string, unknown>
const eventsListInFlight = new Map<string, Promise<EventListResponseProps>>()

function isRecordLike(value: unknown): value is RecordLike {
  return typeof value === 'object' && value !== null
}

function getString(
  value: unknown,
  fallback?: string | null,
): string | null | undefined {
  if (typeof value === 'string') {
    return value
  }

  return fallback
}

function getOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function getNumber(
  value: unknown,
  fallback?: number | null,
): number | null | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  return fallback
}

function getBoolean(
  value: unknown,
  fallback?: boolean | null,
): boolean | null | undefined {
  if (typeof value === 'boolean') {
    return value
  }

  return fallback
}

function getPayloadData<T>(value: unknown): T {
  if (isRecordLike(value) && 'data' in value) {
    return value.data as T
  }

  return value as T
}

function normalizeDiscoveryStatus(status: unknown): EventDiscoveryStatus {
  if (
    status === 'DRAFT' ||
    status === 'PUBLISHED' ||
    status === 'ARCHIVED' ||
    status === 'HIDDEN'
  ) {
    return status
  }

  return 'DRAFT'
}

function normalizeEventTheme(theme: unknown): EventSummaryProps['theme'] {
  switch (theme) {
    case 'SAO_JOAO':
    case 'CARNAVAL':
    case 'REVEILLON':
    case 'COPA_DO_MUNDO':
    case 'VAQUEJADA':
    case 'FESTIVAL':
    case 'OTHER':
      return theme
    default:
      return null
  }
}

function normalizePublicationStatus(status: unknown): ScheduleItemStatus {
  if (status === 'PUBLISHED' || status === 'CANCELLED') {
    return status
  }

  return 'DRAFT'
}

function normalizeScheduleItemType(type: unknown): ScheduleItemType {
  switch (type) {
    case 'GAME_SCREENING':
    case 'CEREMONY':
    case 'OPENING':
    case 'CULTURAL_ACTIVITY':
    case 'ACTIVATION':
    case 'DJ':
    case 'INFO':
    case 'OTHER':
      return type
    default:
      return 'OTHER'
  }
}

function normalizeEventSummary(event: unknown): EventSummaryProps {
  const raw = isRecordLike(event) ? event : {}

  return {
    id: getString(raw.id, '') ?? '',
    title: getString(raw.title, '') ?? '',
    slug: getString(raw.slug, '') ?? '',
    editionYear: getNumber(raw.editionYear, null),
    theme: normalizeEventTheme(raw.theme),
    shortDescription: getString(raw.shortDescription, null),
    description: getString(raw.description, null),
    city: getString(raw.city, null),
    state: getString(raw.state, null),
    startDate: getString(raw.startDate, null),
    endDate: getString(raw.endDate, null),
    coverImageUrl: getString(raw.coverImageUrl, null),
    bannerImageUrl: getString(raw.bannerImageUrl, null),
    officialPlaylistId: getString(raw.officialPlaylistId, null),
    officialUrl: getString(raw.officialUrl, null),
    ticketPageUrl: getString(raw.ticketPageUrl, null),
    shareTitle: getString(raw.shareTitle, null),
    shareDescription: getString(raw.shareDescription, null),
    isFeatured: getBoolean(raw.isFeatured, null),
    discoveryStatus: normalizeDiscoveryStatus(raw.discoveryStatus),
    publishedAt: getString(raw.publishedAt, null),
    createdAt: getString(raw.createdAt, null),
    updatedAt: getString(raw.updatedAt, null),
  }
}

function normalizePerformance(performance: unknown): EventPerformanceProps {
  const raw = isRecordLike(performance) ? performance : {}

  const rawArtist = isRecordLike(raw.artist) ? raw.artist : null

  return {
    id: getString(raw.id, '') ?? '',
    poleId: getOptionalString(raw.poleId),
    artistId: getString(raw.artistId, null),
    title: getString(raw.title, null),
    performanceDate: getString(raw.performanceDate, null),
    startTime: getString(raw.startTime, null),
    endTime: getString(raw.endTime, null),
    sortOrder: getNumber(raw.sortOrder, null),
    isHeadliner: getBoolean(raw.isHeadliner, null),
    note: getString(raw.note, null),
    status: normalizePublicationStatus(raw.status),
    createdAt: getString(raw.createdAt, null),
    updatedAt: getString(raw.updatedAt, null),
    artist: rawArtist
      ? {
          id: getString(rawArtist.id, '') ?? '',
          title: getString(rawArtist.title, '') ?? '',
          slug: getString(rawArtist.slug, null),
          publicPhotoURL: getString(rawArtist.publicPhotoURL, null),
        }
      : null,
  }
}

function normalizeScheduleItem(item: unknown): EventScheduleItemProps {
  const raw = isRecordLike(item) ? item : {}

  return {
    id: getString(raw.id, '') ?? '',
    poleId: getOptionalString(raw.poleId),
    type: normalizeScheduleItemType(raw.type),
    title: getString(raw.title, '') ?? '',
    description: getString(raw.description, null),
    startsAt: getString(raw.startsAt, null),
    endsAt: getString(raw.endsAt, null),
    coverImageUrl: getString(raw.coverImageUrl, null),
    bannerImageUrl: getString(raw.bannerImageUrl, null),
    officialUrl: getString(raw.officialUrl, null),
    sortOrder: getNumber(raw.sortOrder, null),
    status: normalizePublicationStatus(raw.status),
    createdAt: getString(raw.createdAt, null),
    updatedAt: getString(raw.updatedAt, null),
  }
}

function normalizeScheduleEntry(entry: unknown): EventScheduleEntryProps {
  const raw = isRecordLike(entry) ? entry : {}
  const rawArtist = isRecordLike(raw.artist) ? raw.artist : null

  return {
    id: getString(raw.id, '') ?? '',
    kind: raw.kind === 'PERFORMANCE' ? 'PERFORMANCE' : 'SCHEDULE_ITEM',
    poleId: getOptionalString(raw.poleId),
    status: normalizePublicationStatus(raw.status),
    title: getString(raw.title, null) ?? getString(rawArtist?.title, null),
    startsAt: getString(raw.startsAt, null),
    endsAt: getString(raw.endsAt, null),
    sortOrder: getNumber(raw.sortOrder, null),
    type:
      raw.kind === 'SCHEDULE_ITEM' ? normalizeScheduleItemType(raw.type) : null,
    artistName:
      getString(raw.artistName, null) ??
      getString(raw.artistTitle, null) ??
      getString(rawArtist?.title, null),
    isHeadliner: getBoolean(raw.isHeadliner, null),
    description: getString(raw.description, null),
  }
}

function normalizePole(pole: unknown): EventPoleProps {
  const raw = isRecordLike(pole) ? pole : {}

  const rawOfficialPlaylist = isRecordLike(raw.officialPlaylist)
    ? raw.officialPlaylist
    : null
  const rawPerformances = Array.isArray(raw.performances)
    ? raw.performances
    : []
  const rawScheduleItems = Array.isArray(raw.scheduleItems)
    ? raw.scheduleItems
    : []
  const rawSchedule = Array.isArray(raw.schedule) ? raw.schedule : []

  return {
    id: getString(raw.id, '') ?? '',
    eventId: getOptionalString(raw.eventId),
    name: getString(raw.name, '') ?? '',
    slug: getString(raw.slug, '') ?? '',
    description: getString(raw.description, null),
    isMain: getBoolean(raw.isMain, null),
    sortOrder: getNumber(raw.sortOrder, null),
    placeName: getString(raw.placeName, null),
    addressLine: getString(raw.addressLine, null),
    neighborhood: getString(raw.neighborhood, null),
    city: getString(raw.city, null),
    state: getString(raw.state, null),
    latitude: getNumber(raw.latitude, null),
    longitude: getNumber(raw.longitude, null),
    mapsUrl: getString(raw.mapsUrl, null),
    startDate: getString(raw.startDate, null),
    endDate: getString(raw.endDate, null),
    officialPlaylistId: getString(raw.officialPlaylistId, null),
    discoveryStatus: normalizeDiscoveryStatus(raw.discoveryStatus),
    publishedAt: getString(raw.publishedAt, null),
    createdAt: getString(raw.createdAt, null),
    updatedAt: getString(raw.updatedAt, null),
    officialPlaylist: rawOfficialPlaylist
      ? {
          id: getString(rawOfficialPlaylist.id, '') ?? '',
          title: getString(rawOfficialPlaylist.title, '') ?? '',
          visibility: getString(rawOfficialPlaylist.visibility, null),
          musicsCount: getNumber(rawOfficialPlaylist.musicsCount, null),
        }
      : null,
    performances: rawPerformances.map(normalizePerformance),
    scheduleItems: rawScheduleItems.map(normalizeScheduleItem),
    schedule: rawSchedule.map(normalizeScheduleEntry),
  }
}

function normalizePoles(poles: unknown): EventPoleProps[] {
  if (!Array.isArray(poles)) return []
  return poles.map(normalizePole)
}

function normalizePerformances(
  performances: unknown,
  poleId?: string,
): EventPerformanceProps[] {
  if (!Array.isArray(performances)) return []

  return performances.map((performance) => {
    const normalized = normalizePerformance(performance)

    if (poleId && !normalized.poleId) {
      return {
        ...normalized,
        poleId,
      }
    }

    return normalized
  })
}

function normalizeScheduleItems(
  items: unknown,
  poleId?: string,
): EventScheduleItemProps[] {
  if (!Array.isArray(items)) return []

  return items.map((item) => {
    const normalized = normalizeScheduleItem(item)

    if (poleId && !normalized.poleId) {
      return {
        ...normalized,
        poleId,
      }
    }

    return normalized
  })
}

function normalizeEventDetail(payload: unknown): EventDetailProps {
  const raw = getPayloadData<RecordLike>(payload)
  const rawEvent = isRecordLike(raw.event) ? raw.event : raw
  const rawOfficialPlaylist = isRecordLike(raw.officialPlaylist)
    ? raw.officialPlaylist
    : null

  return {
    event: normalizeEventSummary(rawEvent),
    officialPlaylist: rawOfficialPlaylist
      ? {
          id: getString(rawOfficialPlaylist.id, '') ?? '',
          title: getString(rawOfficialPlaylist.title, '') ?? '',
          visibility: getString(rawOfficialPlaylist.visibility, null),
          musicsCount: getNumber(rawOfficialPlaylist.musicsCount, null),
        }
      : null,
    poles: normalizePoles(raw.poles),
  }
}

function normalizeEventList(payload: unknown): EventListResponseProps {
  let rawData: unknown[] = []
  let rawMeta: unknown = {}

  if (Array.isArray(payload)) {
    rawData = payload
  } else if (isRecordLike(payload) && Array.isArray(payload.data)) {
    rawData = payload.data
    rawMeta = payload.meta
  } else if (isRecordLike(payload) && isRecordLike(payload.data)) {
    const nestedData = payload.data
    rawData = Array.isArray(nestedData.data) ? nestedData.data : []
    rawMeta = nestedData.meta
  } else {
    const unwrapped = getPayloadData<unknown>(payload)

    if (Array.isArray(unwrapped)) {
      rawData = unwrapped
    } else if (isRecordLike(unwrapped) && Array.isArray(unwrapped.data)) {
      rawData = unwrapped.data
      rawMeta = unwrapped.meta
    }
  }

  const normalizedMeta = normalizePaginationMeta(
    rawMeta as
      | {
          currentPage?: number
          page?: number
          lastPage?: number
          totalItems?: number
          total?: number
        }
      | undefined,
    rawData.length,
  )

  return {
    data: rawData.map(normalizeEventSummary),
    meta: {
      total: normalizedMeta.totalItems,
      page: normalizedMeta.currentPage || 1,
      lastPage: normalizedMeta.lastPage || 1,
    },
  }
}

function sanitizeText(value?: string) {
  const sanitized = value?.trim()
  return sanitized || undefined
}

function mapEventPayload(
  payload: EventMutationPayloadProps,
): EventMutationPayloadProps {
  return {
    title: payload.title.trim(),
    ...(sanitizeText(payload.slug) ? { slug: payload.slug?.trim() } : {}),
    editionYear: payload.editionYear,
    theme: payload.theme,
    city: payload.city.trim(),
    state: payload.state.trim().slice(0, 2).toUpperCase(),
    startDate: payload.startDate,
    endDate: payload.endDate,
    shortDescription: sanitizeText(payload.shortDescription),
    description: sanitizeText(payload.description),
    coverImageUrl: sanitizeText(payload.coverImageUrl),
    bannerImageUrl: sanitizeText(payload.bannerImageUrl),
    officialPlaylistId: sanitizeText(payload.officialPlaylistId),
    officialUrl: sanitizeText(payload.officialUrl),
    ticketPageUrl: sanitizeText(payload.ticketPageUrl),
    shareTitle: sanitizeText(payload.shareTitle),
    shareDescription: sanitizeText(payload.shareDescription),
    isFeatured: payload.isFeatured ?? false,
  }
}

function mapPolePayload(
  payload: PoleMutationPayloadProps,
): PoleMutationPayloadProps {
  return {
    name: payload.name.trim(),
    ...(sanitizeText(payload.slug) ? { slug: payload.slug?.trim() } : {}),
    city: payload.city.trim(),
    state: payload.state.trim().slice(0, 2).toUpperCase(),
    description: sanitizeText(payload.description),
    isMain: payload.isMain ?? false,
    sortOrder: payload.sortOrder,
    placeName: sanitizeText(payload.placeName),
    addressLine: sanitizeText(payload.addressLine),
    neighborhood: sanitizeText(payload.neighborhood),
    latitude: payload.latitude,
    longitude: payload.longitude,
    mapsUrl: sanitizeText(payload.mapsUrl),
    startDate: payload.startDate,
    endDate: payload.endDate,
    officialPlaylistId: sanitizeText(payload.officialPlaylistId),
  }
}

function mapPerformancePayload(
  payload: PerformanceMutationPayloadProps,
): PerformanceMutationPayloadProps {
  return {
    artistId: payload.artistId,
    performanceDate: payload.performanceDate,
    title: sanitizeText(payload.title),
    startTime: sanitizeText(payload.startTime),
    endTime: sanitizeText(payload.endTime),
    sortOrder: payload.sortOrder,
    isHeadliner: payload.isHeadliner ?? false,
    note: sanitizeText(payload.note),
  }
}

function mapScheduleItemPayload(
  payload: ScheduleItemMutationPayloadProps,
): ScheduleItemMutationPayloadProps {
  return {
    type: payload.type,
    title: payload.title.trim(),
    startsAt: payload.startsAt,
    description: sanitizeText(payload.description),
    endsAt: payload.endsAt || undefined,
    coverImageUrl: sanitizeText(payload.coverImageUrl),
    bannerImageUrl: sanitizeText(payload.bannerImageUrl),
    officialUrl: sanitizeText(payload.officialUrl),
    sortOrder: payload.sortOrder,
  }
}

function normalizeListFromResponse<T>(
  response: AxiosResponse<unknown>,
  mapper: (entry: unknown) => T,
): T[] {
  const payload = getPayloadData<unknown>(response.data)

  if (Array.isArray(payload)) {
    return payload.map(mapper)
  }

  if (isRecordLike(payload) && Array.isArray(payload.data)) {
    return payload.data.map(mapper)
  }

  return []
}

export function extractApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (!isRecordLike(error)) {
    return fallback
  }

  const response = isRecordLike(error.response) ? error.response : null
  const responseData =
    response && isRecordLike(response.data) ? response.data : null

  if (!responseData || !('message' in responseData)) {
    return fallback
  }

  const message = responseData.message

  if (typeof message === 'string' && message.trim().length > 0) {
    return message
  }

  if (Array.isArray(message)) {
    const firstMessage = message.find(
      (entry) => typeof entry === 'string' && entry.trim().length > 0,
    )

    if (typeof firstMessage === 'string') {
      return firstMessage
    }
  }

  return fallback
}

export async function fetchEvents(
  filters: EventListFiltersProps = {},
): Promise<EventListResponseProps> {
  const params = {
    page: filters.page ?? 1,
    limit: filters.limit ?? 20,
    ...(sanitizeText(filters.title)
      ? { title: sanitizeText(filters.title) }
      : {}),
    ...(sanitizeText(filters.state)
      ? { state: sanitizeText(filters.state) }
      : {}),
    ...(filters.year ? { year: filters.year } : {}),
    ...(filters.theme ? { theme: filters.theme } : {}),
    ...(filters.discoveryStatus
      ? { discoveryStatus: filters.discoveryStatus }
      : {}),
  }

  const requestKey = JSON.stringify(params)
  const requestInFlight = eventsListInFlight.get(requestKey)

  if (requestInFlight) {
    return requestInFlight
  }

  const requestPromise = api
    .get('/events', { params })
    .then((response) => normalizeEventList(response.data))
    .finally(() => {
      eventsListInFlight.delete(requestKey)
    })

  eventsListInFlight.set(requestKey, requestPromise)

  return requestPromise
}

export async function fetchEventDetail(
  eventId: string,
): Promise<EventDetailProps> {
  const response = await api.get(`/events/${eventId}`)
  return normalizeEventDetail(response.data)
}

export async function createEvent(payload: EventMutationPayloadProps) {
  const response = await api.post('/events', mapEventPayload(payload))
  return normalizeEventDetail(response.data)
}

export async function updateEvent(
  eventId: string,
  payload: EventMutationPayloadProps,
) {
  const response = await api.patch(
    `/events/${eventId}`,
    mapEventPayload(payload),
  )
  return normalizeEventDetail(response.data)
}

export async function publishEvent(eventId: string) {
  await api.patch(`/events/${eventId}/publish`)
}

export async function unpublishEvent(eventId: string) {
  await api.patch(`/events/${eventId}/unpublish`)
}

export async function deleteEvent(eventId: string) {
  await api.delete(`/events/${eventId}`)
}

export async function archiveEvent(eventId: string) {
  await api.patch(`/events/${eventId}/archive`)
}

export async function fetchEventPoles(
  eventId: string,
): Promise<EventPoleProps[]> {
  const response = await api.get(`/events/${eventId}/poles`)
  return normalizeListFromResponse(response, normalizePole)
}

export async function createEventPole(
  eventId: string,
  payload: PoleMutationPayloadProps,
) {
  const response = await api.post(
    `/events/${eventId}/poles`,
    mapPolePayload(payload),
  )
  return normalizePole(getPayloadData(response.data))
}

export async function updateEventPole(
  eventId: string,
  poleId: string,
  payload: PoleMutationPayloadProps,
) {
  const response = await api.patch(
    `/events/${eventId}/poles/${poleId}`,
    mapPolePayload(payload),
  )

  return normalizePole(getPayloadData(response.data))
}

export async function publishEventPole(eventId: string, poleId: string) {
  await api.patch(`/events/${eventId}/poles/${poleId}/publish`)
}

export async function unpublishEventPole(eventId: string, poleId: string) {
  await api.patch(`/events/${eventId}/poles/${poleId}/unpublish`)
}

export async function deleteEventPole(eventId: string, poleId: string) {
  await api.delete(`/events/${eventId}/poles/${poleId}`)
}

export async function fetchPolePerformances(
  eventId: string,
  poleId: string,
): Promise<EventPerformanceProps[]> {
  const response = await api.get(
    `/events/${eventId}/poles/${poleId}/performances`,
  )
  return normalizePerformances(
    normalizeListFromResponse(response, normalizePerformance),
    poleId,
  )
}

export async function createPolePerformance(
  eventId: string,
  poleId: string,
  payload: PerformanceMutationPayloadProps,
) {
  const response = await api.post(
    `/events/${eventId}/poles/${poleId}/performances`,
    mapPerformancePayload(payload),
  )

  return normalizePerformance(getPayloadData(response.data))
}

export async function updatePolePerformance(
  eventId: string,
  poleId: string,
  performanceId: string,
  payload: PerformanceMutationPayloadProps,
) {
  const response = await api.patch(
    `/events/${eventId}/poles/${poleId}/performances/${performanceId}`,
    mapPerformancePayload(payload),
  )

  return normalizePerformance(getPayloadData(response.data))
}

export async function publishPolePerformance(
  eventId: string,
  poleId: string,
  performanceId: string,
) {
  await api.patch(
    `/events/${eventId}/poles/${poleId}/performances/${performanceId}/publish`,
  )
}

export async function unpublishPolePerformance(
  eventId: string,
  poleId: string,
  performanceId: string,
) {
  await api.patch(
    `/events/${eventId}/poles/${poleId}/performances/${performanceId}/unpublish`,
  )
}

export async function cancelPolePerformance(
  eventId: string,
  poleId: string,
  performanceId: string,
) {
  await api.patch(
    `/events/${eventId}/poles/${poleId}/performances/${performanceId}/cancel`,
  )
}

export async function deletePolePerformance(
  eventId: string,
  poleId: string,
  performanceId: string,
) {
  await api.delete(
    `/events/${eventId}/poles/${poleId}/performances/${performanceId}`,
  )
}

export async function fetchPoleScheduleItems(
  eventId: string,
  poleId: string,
): Promise<EventScheduleItemProps[]> {
  const response = await api.get(
    `/events/${eventId}/poles/${poleId}/schedule-items`,
  )

  return normalizeScheduleItems(
    normalizeListFromResponse(response, normalizeScheduleItem),
    poleId,
  )
}

export async function createPoleScheduleItem(
  eventId: string,
  poleId: string,
  payload: ScheduleItemMutationPayloadProps,
) {
  const response = await api.post(
    `/events/${eventId}/poles/${poleId}/schedule-items`,
    mapScheduleItemPayload(payload),
  )

  return normalizeScheduleItem(getPayloadData(response.data))
}

export async function updatePoleScheduleItem(
  eventId: string,
  poleId: string,
  scheduleItemId: string,
  payload: ScheduleItemMutationPayloadProps,
) {
  const response = await api.patch(
    `/events/${eventId}/poles/${poleId}/schedule-items/${scheduleItemId}`,
    mapScheduleItemPayload(payload),
  )

  return normalizeScheduleItem(getPayloadData(response.data))
}

export async function publishPoleScheduleItem(
  eventId: string,
  poleId: string,
  scheduleItemId: string,
) {
  await api.patch(
    `/events/${eventId}/poles/${poleId}/schedule-items/${scheduleItemId}/publish`,
  )
}

export async function unpublishPoleScheduleItem(
  eventId: string,
  poleId: string,
  scheduleItemId: string,
) {
  await api.patch(
    `/events/${eventId}/poles/${poleId}/schedule-items/${scheduleItemId}/unpublish`,
  )
}

export async function cancelPoleScheduleItem(
  eventId: string,
  poleId: string,
  scheduleItemId: string,
) {
  await api.patch(
    `/events/${eventId}/poles/${poleId}/schedule-items/${scheduleItemId}/cancel`,
  )
}

export async function deletePoleScheduleItem(
  eventId: string,
  poleId: string,
  scheduleItemId: string,
) {
  await api.delete(
    `/events/${eventId}/poles/${poleId}/schedule-items/${scheduleItemId}`,
  )
}

interface FetchArtistsOptionsParams {
  query?: string
  limit?: number
  page?: number
}

export async function fetchArtistsOptions(
  params: FetchArtistsOptionsParams = {},
): Promise<ArtistOptionProps[]> {
  const response = await api.get('/artists', {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 50,
      ...(sanitizeText(params.query)
        ? { title: sanitizeText(params.query) }
        : {}),
    },
  })

  const payload = getPayloadData<unknown>(response.data)

  let rawArtists: unknown[] = []

  if (Array.isArray(payload)) {
    rawArtists = payload
  } else if (isRecordLike(payload) && Array.isArray(payload.data)) {
    rawArtists = payload.data
  } else if (isRecordLike(payload) && isRecordLike(payload.data)) {
    rawArtists = Array.isArray(payload.data.data) ? payload.data.data : []
  }

  return rawArtists
    .map((artist) => {
      const rawArtist = isRecordLike(artist) ? artist : {}

      return {
        id: getString(rawArtist.id, '') ?? '',
        title: getString(rawArtist.title, '') ?? '',
      }
    })
    .filter((artist) => artist.id && artist.title)
}
